// src/components/Topbar.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth, firebaseSignOut, db } from "../firebase";
import { collection, query, where, getDocs, limit } from "firebase/firestore";

export default function Topbar({ isSidebarCollapsed }) {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState("");
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    // Get current user from Firebase Auth
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        // Get display name or email prefix
        const name = currentUser.displayName || currentUser.email?.split('@')[0] || "Admin";
        setUserName(name);
      } else {
        setUser(null);
        setUserName("");
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Fetch notifications when user is logged in
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      // Fetch expiring documents (compliance alerts)
      const today = new Date();
      const in14Days = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
      
      const docsSnap = await getDocs(collection(db, "documents"));
      const expiringDocs = docsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(doc => {
          if (!doc.expiry_date || doc.owner_type !== "truck") return false;
          const expDate = new Date(doc.expiry_date);
          return expDate >= today && expDate <= in14Days;
        })
        .slice(0, 3)
        .map(doc => ({
          id: `doc-${doc.id}`,
          type: "compliance",
          message: `${doc.doc_type || "Document"} for ${doc.owner_id} expires soon`,
          read: false,
          link: "/documents",
          daysLeft: Math.ceil((new Date(doc.expiry_date) - today) / (1000 * 60 * 60 * 24))
        }));

      // Fetch active trips notifications
      const tripsSnap = await getDocs(collection(db, "trips"));
      const activeTrips = tripsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(trip => trip.status === "Active")
        .slice(0, 2)
        .map(trip => ({
          id: `trip-${trip.id}`,
          type: "trip",
          message: `Trip ${trip.trip_code} is in progress`,
          read: false,
          link: "/trips"
        }));

      // Combine notifications
      const allNotifications = [...expiringDocs, ...activeTrips];
      
      // Check localStorage for previously read notifications
      const readNotifications = JSON.parse(localStorage.getItem("read_notifications") || "[]");
      
      const notificationsWithReadStatus = allNotifications.map(notif => ({
        ...notif,
        read: readNotifications.includes(notif.id)
      }));
      
      setNotifications(notificationsWithReadStatus);
      setUnreadCount(notificationsWithReadStatus.filter(n => !n.read).length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      // Fallback to mock data if Firebase fetch fails
      setNotifications([
        { id: 1, type: "info", message: "Welcome to Fleet Manager", read: false, link: "/" }
      ]);
      setUnreadCount(1);
    }
  };

  const handleLogout = async () => {
    try {
      await firebaseSignOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Save to localStorage
    const readNotifications = JSON.parse(localStorage.getItem("read_notifications") || "[]");
    if (!readNotifications.includes(notificationId)) {
      readNotifications.push(notificationId);
      localStorage.setItem("read_notifications", JSON.stringify(readNotifications));
    }
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    
    // Save all to localStorage
    const allIds = notifications.map(n => n.id);
    localStorage.setItem("read_notifications", JSON.stringify(allIds));
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    navigate(notification.link);
    setShowNotifications(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "compliance": return "📄";
      case "trip": return "✈️";
      case "service": return "🔧";
      case "complaint": return "⚠️";
      default: return "🔔";
    }
  };

  return (
    <div className={`topbar ${isSidebarCollapsed ? "expanded" : ""}`}>
      <div className="topbar-title">
        <span className="company-name">Morgan BRB</span>
        <span className="fleet-badge">Fleet Management System</span>
      </div>

      <div className="topbar-actions">
        {/* Notifications */}
        <div className="notification-wrapper" ref={notificationRef}>
          <button
            className="notification-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            🔔
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h4>Notifications</h4>
                {unreadCount > 0 && (
                  <button onClick={markAllAsRead} className="mark-all-read">
                    Mark all as read
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="notification-empty">No new notifications</div>
                ) : (
                  notifications.map(notif => (
                    <div
                      key={notif.id}
                      className={`notification-item ${!notif.read ? "unread" : ""}`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <div className="notification-icon">
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className="notification-content">
                        <p>{notif.message}</p>
                        {notif.daysLeft && (
                          <span className="notification-time">{notif.daysLeft} days left</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Profile */}
        <div className="profile-wrapper" ref={profileRef}>
          <button
            className="profile-btn"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
          >
            👤 {userName} ▼
          </button>

          {showProfileMenu && (
            <div className="profile-dropdown">
              <div className="profile-info">
                <strong>{user?.displayName || user?.email || "Admin User"}</strong>
                <span>{user?.email || "admin@morganbrb.com"}</span>
                <span className="profile-role">Fleet Administrator</span>
              </div>
              <hr />
              <button onClick={() => navigate("/settings")}>
                ⚙️ Settings
              </button>
              <button onClick={() => navigate("/profile")}>
                👤 My Profile
              </button>
              <hr />
              <button onClick={handleLogout} className="logout-btn">
                🚪 Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}